import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import type { TrackEvent } from "@/types/generated";
import { Button } from "../ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { router } from "@inertiajs/react";

interface RaceCommentaryCardProps {
  trackEvent: TrackEvent;
}

export function RaceCommentaryCard({ trackEvent }: RaceCommentaryCardProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  if (!trackEvent.race_commentary) return null;

  async function handleGenerate(regenerate = false) {
    setIsGenerating(true);
    try {
      const url = regenerate
        ? `/event/${trackEvent.id}/generate_commentary?regenerate=true`
        : `/event/${trackEvent.id}/generate_commentary`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      const data = await response.json();
      if (data.success) {
        const action = regenerate ? "regeneration" : "generation";
        toast.success(`Commentary ${action} started!`, {
          description: "Refresh the page in a few seconds to see it.",
        });
        setTimeout(() => router.reload(), 3000);
      } else {
        toast.error("Error", { description: data.error });
      }
    } catch {
      toast.error("Error", {
        description: "Failed to generate commentary. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Race Commentary</CardTitle>
          </div>
          {trackEvent.can_manage_commentary && (
            <Button
              onClick={() => handleGenerate(true)}
              disabled={isGenerating}
              variant="ghost"
              size="sm"
            >
              <RefreshCw
                className={`w-4 h-4 mr-1 ${isGenerating ? "animate-spin" : ""}`}
              />
              Regenerate
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-foreground leading-relaxed">
          "{trackEvent.race_commentary.commentary}"
        </p>
      </CardContent>
    </Card>
  );
}
